import { HStack, Text, VStack } from '@chakra-ui/react';
import { timeDiff } from '@utils/date.utils';
import { addMinutes } from 'date-fns';
import { capitalize } from 'lodash';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { DateTimeEnum, DateTimeRange } from '@/types/dateTime.types';

import CustomInputDateTime from './CustomInputDateTime';

interface CustomInputDateTimeRange {
  range?: DateTimeRange;
  variant?: 'filled' | 'default';
  minDate?: Date;
  maxDate?: Date;
  singleLine?: boolean;
  showSecond?: boolean;
  invalidFrom?: boolean;
  invalidTo?: boolean;
  textColor?: string;
  onChange?: (range: DateTimeRange) => void;
}

function CustomInputDateTimeRange({
  range,
  onChange,
  singleLine = true,
  variant = 'default',
  showSecond = true,
  minDate,
  maxDate,
  invalidFrom,
  invalidTo,
  textColor = 'neutral.white',
}: Readonly<CustomInputDateTimeRange>) {
  const [dateRange, setDateRange] = useState(range ?? { from: addMinutes(Date.now(), -10), to: new Date() });
  const { formatMessage } = useIntl();

  function handleChange(position: string, value: string) {
    let newRange: { from: Date; to: Date } = { ...dateRange, [position]: new Date(value) };
    if (position === 'from' && timeDiff(newRange.to, newRange.from) < 0) {
      newRange = {
        ...newRange,
        to: addMinutes(newRange.from, 10),
      };
    }
    if (position === 'to' && timeDiff(newRange.to, newRange.from) < 0) {
      newRange = {
        ...newRange,
        from: addMinutes(newRange.to, -10),
      };
    }
    setDateRange(newRange);
    onChange?.(newRange);
  }

  useEffect(() => {
    range && setDateRange(range);
  }, [range]);

  return singleLine ? (
    <HStack alignItems="center" gap={2} width="100%">
      <Text textStyle="md" color="neutral.300">
        <FormattedMessage id="global.from" />
      </Text>
      <CustomInputDateTime
        type={DateTimeEnum.DATE_TIME}
        min={minDate?.toString()}
        max={maxDate?.toString()}
        value={dateRange.from.toString()}
        variant={variant}
        showSecond={showSecond}
        invalid={invalidFrom}
        onChange={(date) => handleChange('from', date)}
      />
      <Text textStyle="md" color="neutral.300">
        <FormattedMessage id="global.to" />
      </Text>
      <CustomInputDateTime
        type={DateTimeEnum.DATE_TIME}
        min={dateRange.from.toString()}
        max={maxDate?.toString()}
        value={dateRange.to.toString()}
        variant={variant}
        showSecond={showSecond}
        invalid={invalidTo}
        onChange={(date) => handleChange('to', date)}
      />
    </HStack>
  ) : (
    <HStack gap={2} width="100%" alignItems="start">
      <VStack gap={1.5} height="stretch" justifyContent="space-around">
        <Text color={textColor}>
          <FormattedMessage id="global.from" />
        </Text>
        <Text color={textColor}>{capitalize(formatMessage({ id: 'global.to' }))}</Text>
      </VStack>
      <VStack gap={1.5} flex={1}>
        <CustomInputDateTime
          type={DateTimeEnum.DATE_TIME}
          min={minDate?.toString()}
          max={maxDate?.toString()}
          value={dateRange.from.toString()}
          variant={variant}
          showSecond={showSecond}
          invalid={invalidFrom}
          onChange={(date) => handleChange('from', date)}
        />
        <CustomInputDateTime
          type={DateTimeEnum.DATE_TIME}
          min={dateRange.from.toString()}
          max={maxDate?.toString()}
          value={dateRange.to.toString()}
          variant={variant}
          showSecond={showSecond}
          invalid={invalidTo}
          onChange={(date) => handleChange('to', date)}
        />
      </VStack>
    </HStack>
  );
}

export default CustomInputDateTimeRange;
